import environment from "@app/config/environment";
import type {
  AgentClientMessage,
  AgentExtrasPayload,
  AgentServerAction,
  AgentServerMessage,
  BaseAgentServerMessage
} from "@globals";
import { type GenericSchema, any, object, optional, picklist, safeParse } from "valibot";

// A frame whose action is missing here fails validation and is dropped before any handler sees
// it, which looks exactly like the server never sent it. `UnlistedAction` turns that into a
// compile error instead: adding a server message means adding it here too.
const agentServerActions = [
  "authentication",
  "agent.response.chunk",
  "agent.tool_use",
  "agent.tool_approval_required",
  "agent.extras",
  "agent.turn.started",
  "agent.turn.done",
  "agent.error",
  "agent.ready",
  "agent.pong"
] as const satisfies readonly AgentServerAction[];

type UnlistedAction = Exclude<AgentServerAction, (typeof agentServerActions)[number]>;
type AssertAllActionsListed<T extends never> = T;
export type _AllAgentServerActionsListed = AssertAllActionsListed<UnlistedAction>;

const agentServerMessageSchema = object({
  action: picklist(agentServerActions),
  payload: optional(any())
}) satisfies GenericSchema<BaseAgentServerMessage>;

/**
 * Response item type for extras - union of all item types.
 * Delivered via agent.extras during streaming; lost on page refresh.
 */
export type AgentExtraItem = AgentExtrasPayload["items"][number];

export type ConnectionState = "disconnected" | "connecting" | "reconnecting" | "connected";

type AgentWebSocketListener = (message: AgentServerMessage) => void;
type StateChangeListener = (state: ConnectionState) => void;

/**
 * WebSocket client for agent communication.
 * Uses the /ws/agent/ endpoint for real-time AI agent interactions.
 *
 * Automatically reconnects after any unintentional close (idle timeout, network blip).
 * Messages sent while not connected are queued and flushed once agent.ready arrives.
 */
class AgentWebSocketClient {
  private ws: WebSocket | null = null;
  private pingTimer: number | null = null;
  private reconnectTimeout: number | null = null;
  // Set to true by connect(), false by close(). Drives auto-reconnect.
  private shouldReconnect = false;
  private readonly reconnectDelay = 2000;
  // Stored so reconnects can rebuild the URL without external help.
  private storedTeamId: string | null = null;
  // Messages sent while the socket is not open are held here and flushed on agent.ready.
  private pendingQueue: AgentClientMessage[] = [];
  private listeners = new Set<AgentWebSocketListener>();
  private stateListeners = new Set<StateChangeListener>();
  private _state: ConnectionState = "disconnected";
  private getToken: (() => Promise<string>) | null = null;

  get state(): ConnectionState {
    return this._state;
  }

  private setState(state: ConnectionState) {
    this._state = state;
    for (const listener of this.stateListeners) {
      listener(state);
    }
  }

  /** Connects to the team, keeping the current connection if it already serves that team. */
  async connect(getToken: () => Promise<string>, teamId: string | null) {
    this.getToken = getToken;
    if (this.shouldReconnect && this.storedTeamId === teamId) {
      return;
    }
    this.storedTeamId = teamId;
    this.shouldReconnect = true;
    this.cleanup();
    this.setState("connecting");
    await this.connectInternal();
  }

  /** Intentional teardown — stops reconnect loop and clears the queue. */
  close() {
    // eslint-disable-next-line no-console
    console.log("🔒 Closing agent WebSocket connection…");
    this.shouldReconnect = false;
    this.pendingQueue = [];
    this.cleanup();
    this.setState("disconnected");
  }

  /**
   * Send a message. If the socket is not yet open (connecting / reconnecting),
   * the message is queued and delivered once agent.ready fires.
   */
  send(message: AgentClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      this.pendingQueue.push(message);
    }
  }

  onServerMessage(listener: AgentWebSocketListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStateChange(listener: StateChangeListener): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  private async connectInternal() {
    try {
      if (!this.getToken) {
        throw new Error("AgentWebSocket not initialized: getToken is not set");
      }
      const token = await this.getToken();
      // While we waited for the token, the socket was closed or another connect opened one.
      if (!this.shouldReconnect || this.ws) {
        return;
      }
      // Scope the WS to the resource's team — a missing team_id makes write ops 403.
      const teamParam = this.storedTeamId ? `&team_id=${this.storedTeamId}` : "";
      const url = `${environment.ATI_API_BASE_URL}/ws/agent/?token=Bearer ${token}${teamParam}`;
      this.ws = new WebSocket(url);
      this.ws.onopen = this.onOpen;
      this.ws.onmessage = this.onMessage;
      this.ws.onclose = this.onClose;
      this.ws.onerror = this.onError;
    } catch (error) {
      this.handleDisconnection("Failed to connect to agent WebSocket", error);
    }
  }

  /** Detaches handlers and cancels pending timers without changing shouldReconnect or the queue. */
  private cleanup() {
    this.stopPing();
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    if (this.reconnectTimeout !== null) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private handleDisconnection(reason: string, error?: unknown) {
    const level = error ? "error" : "log";
    // eslint-disable-next-line no-console
    console[level](`⚠️ ${reason}`, error ?? "");
    this.cleanup();
    if (this.shouldReconnect) {
      this.setState("reconnecting");
      this.scheduleReconnect();
    } else {
      this.setState("disconnected");
    }
  }

  private scheduleReconnect() {
    this.reconnectTimeout = window.setTimeout(() => {
      this.reconnectTimeout = null;
      // eslint-disable-next-line no-console
      console.log("🔁 Reconnecting agent WebSocket…");
      void this.connectInternal();
    }, this.reconnectDelay);
  }

  private flushQueue() {
    const queued = this.pendingQueue.splice(0);
    for (const message of queued) {
      this.send(message);
    }
  }

  private onOpen = (event: Event) => {
    if (event.target !== this.ws) {
      return;
    }
    // eslint-disable-next-line no-console
    console.log("✅ Connected to agent WebSocket");
    this.startPing();
  };

  private onMessage = (event: MessageEvent) => {
    if (typeof event.data !== "string") {
      return;
    }
    if (event.target !== this.ws) {
      return;
    }

    try {
      const data = safeParse(agentServerMessageSchema, JSON.parse(event.data));

      if (data.issues) {
        // eslint-disable-next-line no-console
        console.error("❌ Failed to parse agent WebSocket message");
        return;
      }

      // Cast is safe: valibot validates action is a valid AgentServerAction
      const message = data.output as AgentServerMessage;

      if (message.action === "agent.pong" || message.action === "authentication") {
        return;
      }

      if (message.action === "agent.ready") {
        this.setState("connected");
        // Flush any messages queued while the socket was down.
        this.flushQueue();
        return;
      }

      for (const listener of this.listeners) {
        listener(message);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("❌ Failed to parse agent WebSocket message", error);
    }
  };

  private onClose = (event: CloseEvent) => {
    if (event.target !== this.ws) {
      return;
    }
    this.handleDisconnection("Agent WebSocket connection closed. Attempting to reconnect…");
  };

  private onError = (event: Event) => {
    if (event.target !== this.ws) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error("❌ Agent WebSocket error", event);
    this.ws?.close();
  };

  private startPing() {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ action: "agent.ping" });
    }, 15_000);
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }
}

const agentWebSocket = new AgentWebSocketClient();

export default agentWebSocket;
